/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React, {memo} from 'react';
import {CircuitSummary} from '@ion/core/src/types/entities';
import {isCircuitFaulty, isCircuitUnknown} from '../../src/components/smarterTrailer/helper';
import {dlsColor} from '../theme/theme';
import {useThemeMode} from '../theme/useThemeContext';

export interface LightOutDetectionIconProps {
  ground: string;
  clearanceSideMarkers: string;
  leftTurnHazard: string;
  stopLamps: string;
  rightTurnHazard: string;
  tailRunningLights: string;
  auxiliary: string;
  onConnectorHover: (connector: string) => void;
  isLargeVariant?: boolean;
}

export enum IconSize {
  Large = 180,
  Small = 116
}

/**
 * @LightOutDetectionIcon
 *
 * The LightOutDetectionIcon component is a SVG icon that displays the status of the trailer's 7-pin connector.
 *
 * Light Out Detection Sensor is a Smarter Trailer feature that detects when a trailer's
 * 7-way circuits are connected / disconnected / unknown.
 *
 * @param ground - The status of the ground connector.
 * @param clearanceSideMarkers - The status of the clearance side markers connector.
 * @param leftTurnHazard - The status of the left turn hazard connector.
 * @param stopLamps - The status of the stop lamps connector.
 * @param rightTurnHazard - The status of the right turn hazard connector.
 * @param tailRunningLights - The status of the tail running lights connector.
 * @param auxiliary - The status of the auxiliary connector.
 * @param onConnectorHover - A callback function that is called when the user hovers over a connector.
 * @param isLargeVariant - A boolean that determines whether the icon is displayed in a large or small variant.
 *
 * @example
 * ```typescript
 *
 * import {LightOutDetectionIcon} from '@ion/dls/src/icons/';
 *
 * <LightOutDetectionIcon
 *   ground="connected"
 *   clearanceSideMarkers="disconnected"
 *   leftTurnHazard="unknown"
 *   stopLamps="connected"
 *   rightTurnHazard="connected"
 *   tailRunningLights="connected"
 *   auxiliary="connected"
 *   onConnectorHover={onConnectorHover}
 *   isLargeVariant={isPhone} />
 *
 * ```
 * @returns {JSX.Element}
 *
 * @see {@link https://www.figma.com/file/nxLceD5GsSmmWrkD3OEIif/ION-4087?node-id=1025%3A20569&t=MQUtZ9HvUyzfTIN5-0|Figma}
 */
const LightOutDetectionIcon = ({
  ground,
  clearanceSideMarkers,
  leftTurnHazard,
  stopLamps,
  rightTurnHazard,
  tailRunningLights,
  auxiliary,
  onConnectorHover,
  isLargeVariant
}: LightOutDetectionIconProps) => {
  // re-renders on a theme mode change so the colors below re-resolve ...
  useThemeMode();
  // The following are the colors used for each connector state.
  const connectorStateColors = {
    [CircuitSummary.GOOD] : {
      connectorInteriorStroke : dlsColor('grey3'),
      connectorPerimeterFill  : dlsColor('grey5'),
      connectorInteriorFill   : dlsColor('grey8'),
      connectorIdentifier     : dlsColor('black')
    },
    [CircuitSummary.FAULT] : {
      connectorInteriorStroke : dlsColor('caRed'),
      connectorPerimeterFill  : dlsColor('caRed'),
      connectorInteriorFill   : dlsColor('caRed'),
      connectorIdentifier     : dlsColor('white')
    }
  };

  const getConnectorState = (circuit) => {
    if (isCircuitFaulty(circuit)) {
      return CircuitSummary.FAULT;
    } else {
      return CircuitSummary.GOOD;
    }
  };

  const size = isLargeVariant ? IconSize.Large : IconSize.Small;

  return (
    <svg width={size} height={size} viewBox="0 0 116 118" fill="none">
      <path d="M58 116.914C89.4802 116.914 115 91.3943 115 59.9141C115 28.4338 89.4802 2.91406 58 2.91406C26.5198 2.91406 1 28.4338 1 59.9141C1 91.3943 26.5198 116.914 58 116.914Z"
        fill={dlsColor('grey8')}
        stroke={dlsColor('grey3')}
        strokeWidth="0.5"/>
      <mask id="mask0_1025_21155" style={{maskType: 'alpha'}} maskUnits="userSpaceOnUse" x="31" y="0" width="54" height="55">
        <rect x="31.5" y="1.41406" width="53" height="53" fill={dlsColor('grey8')} stroke={dlsColor('grey3')}/>
      </mask>
      <g mask="url(#mask0_1025_21155)">
        <path d="M58 113.914C87.8234 113.914 112 89.7374 112 59.9141C112 30.0907 87.8234 5.91406 58 5.91406C28.1766 5.91406 4 30.0907 4 59.9141C4 89.7374 28.1766 113.914 58 113.914Z" fill={dlsColor('grey8')} stroke={dlsColor('grey3')} strokeWidth="0.5" />
        <line x1="31.25" y1="12.9141" x2="31.25" y2="19.9141" stroke={dlsColor('grey3')} strokeWidth="0.5" />
        <line x1="84.75" y1="12.9141" x2="84.75" y2="19.9141" stroke={dlsColor('grey3')} strokeWidth="0.5" />
      </g>
      <path d="M58 109.914C85.6142 109.914 108 87.5283 108 59.9141C108 32.2998 85.6142 9.91406 58 9.91406C30.3858 9.91406 8 32.2998 8 59.9141C8 87.5283 30.3858 109.914 58 109.914Z" fill={dlsColor('grey8')} stroke={dlsColor('grey3')} strokeWidth="0.5" />
      <path d="M57.9987 107.138C84.0794 107.138 105.222 85.9954 105.222 59.9147C105.222 33.834 84.0794 12.6914 57.9987 12.6914C31.918 12.6914 10.7754 33.834 10.7754 59.9147C10.7754 85.9954 31.918 107.138 57.9987 107.138Z" fill={dlsColor('grey8')} stroke={dlsColor('grey3')} strokeWidth="0.5" />

      {/* 1. Ground Connector */}
      <path d="M58.0002 37.3071C63.7632 37.3071 68.435 32.6353 68.435 26.8723C68.435 21.1093 63.7632 16.4375 58.0002 16.4375C52.2372 16.4375 47.5654 21.1093 47.5654 26.8723C47.5654 32.6353 52.2372 37.3071 58.0002 37.3071Z"
        fill={connectorStateColors?.[getConnectorState(ground)]?.connectorPerimeterFill}
        stroke={dlsColor('grey3')}
        strokeWidth="0.5"/>
      {/* Ground Connector Outline */}
      <path d="M58.0001 34.463C62.1913 34.463 65.589 31.0653 65.589 26.8741C65.589 22.6828 62.1913 19.2852 58.0001 19.2852C53.8088 19.2852 50.4111 22.6828 50.4111 26.8741C50.4111 31.0653 53.8088 34.463 58.0001 34.463Z"
        fill={connectorStateColors?.[getConnectorState(ground)]?.connectorInteriorFill}
        stroke={connectorStateColors?.[getConnectorState(ground)]?.connectorInteriorStroke}
        strokeWidth="0.5"/>
      {/* This is the Number 1 text within the Ground Connector */}
      <path d="M58.8095 22.3359V30.9141H57.7255V23.6895L55.54 24.4863V23.5078L58.6396 22.3359H58.8095Z"
        fill={connectorStateColors?.[getConnectorState(ground)]?.connectorIdentifier}/>

      {/* 2. Clearance / Side Markers Connector */}
      <path d="M86.6955 54.6977C92.4585 54.6977 97.1303 50.0259 97.1303 44.2629C97.1303 38.4999 92.4585 33.8281 86.6955 33.8281C80.9326 33.8281 76.2607 38.4999 76.2607 44.2629C76.2607 50.0259 80.9326 54.6977 86.6955 54.6977Z"
        fill={connectorStateColors?.[getConnectorState(clearanceSideMarkers)]?.connectorPerimeterFill}
        stroke={dlsColor('grey3')}
        strokeWidth="0.5"/>
      <path d="M86.6954 51.8536C90.8866 51.8536 94.2843 48.456 94.2843 44.2647C94.2843 40.0735 90.8866 36.6758 86.6954 36.6758C82.5041 36.6758 79.1064 40.0735 79.1064 44.2647C79.1064 48.456 82.5041 51.8536 86.6954 51.8536Z"
        fill={connectorStateColors?.[getConnectorState(clearanceSideMarkers)]?.connectorInteriorFill}
        stroke={connectorStateColors?.[getConnectorState(clearanceSideMarkers)]?.connectorInteriorStroke}
        strokeWidth="0.5"/>
      <path d="M90.0546 47.5469V48.4375H84.4707V47.6582L87.2656 44.5469C87.6093 44.1641 87.875 43.8398 88.0625 43.5742C88.2539 43.3047 88.3867 43.0645 88.4609 42.8535C88.539 42.6387 88.5781 42.4199 88.5781 42.1973C88.5781 41.916 88.5195 41.6621 88.4023 41.4355C88.289 41.2051 88.1211 41.0215 87.8984 40.8848C87.6757 40.748 87.4062 40.6797 87.0898 40.6797C86.7109 40.6797 86.3945 40.7539 86.1406 40.9023C85.8906 41.0469 85.7031 41.25 85.5781 41.5117C85.4531 41.7734 85.3906 42.0742 85.3906 42.4141H84.3066C84.3066 41.9336 84.4121 41.4941 84.623 41.0957C84.8339 40.6973 85.1464 40.3809 85.5605 40.1465C85.9746 39.9082 86.4843 39.7891 87.0898 39.7891C87.6289 39.7891 88.0898 39.8848 88.4726 40.0762C88.8554 40.2637 89.1484 40.5293 89.3515 40.873C89.5586 41.2129 89.6621 41.6113 89.6621 42.0684C89.6621 42.3184 89.6191 42.5723 89.5332 42.8301C89.4511 43.084 89.3359 43.3379 89.1875 43.5918C89.0429 43.8457 88.873 44.0957 88.6777 44.3418C88.4863 44.5879 88.2812 44.8301 88.0625 45.0684L85.7773 47.5469H90.0546Z"
        fill={connectorStateColors?.[getConnectorState(clearanceSideMarkers)]?.connectorIdentifier}/>

      {/* 3. This is the Left Turn & Hazard Connector graphic */}
      <path d="M86.6955 86.0024C92.4585 86.0024 97.1303 81.3306 97.1303 75.5676C97.1303 69.8046 92.4585 65.1328 86.6955 65.1328C80.9326 65.1328 76.2607 69.8046 76.2607 75.5676C76.2607 81.3306 80.9326 86.0024 86.6955 86.0024Z"
        fill={connectorStateColors?.[getConnectorState(leftTurnHazard)]?.connectorPerimeterFill}
        stroke={dlsColor('grey3')}
        strokeWidth="0.5"/>
      <path d="M86.6954 83.1583C90.8866 83.1583 94.2843 79.7607 94.2843 75.5694C94.2843 71.3781 90.8866 67.9805 86.6954 67.9805C82.5041 67.9805 79.1064 71.3781 79.1064 75.5694C79.1064 79.7607 82.5041 83.1583 86.6954 83.1583Z"
        fill={connectorStateColors?.[getConnectorState(leftTurnHazard)]?.connectorInteriorFill}
        stroke={connectorStateColors?.[getConnectorState(leftTurnHazard)]?.connectorInteriorStroke}
        strokeWidth="0.5"/>
      <path d="M86.0468 74.9434H86.8203C87.1992 74.9434 87.5117 74.8809 87.7578 74.7559C88.0078 74.627 88.1933 74.4531 88.3144 74.2344C88.4394 74.0117 88.5019 73.7617 88.5019 73.4844C88.5019 73.1562 88.4472 72.8809 88.3378 72.6582C88.2285 72.4355 88.0644 72.2676 87.8457 72.1543C87.6269 72.041 87.3496 71.9844 87.0136 71.9844C86.7089 71.9844 86.4394 72.0449 86.205 72.166C85.9746 72.2832 85.7929 72.4512 85.6601 72.6699C85.5312 72.8887 85.4668 73.1465 85.4668 73.4434H84.3828C84.3828 73.0098 84.4921 72.6152 84.7109 72.2598C84.9296 71.9043 85.2363 71.6211 85.6308 71.4102C86.0293 71.1992 86.4902 71.0938 87.0136 71.0938C87.5293 71.0938 87.9804 71.1855 88.3671 71.3691C88.7539 71.5488 89.0546 71.8184 89.2695 72.1777C89.4843 72.5332 89.5918 72.9766 89.5918 73.5078C89.5918 73.7227 89.541 73.9531 89.4394 74.1992C89.3418 74.4414 89.1875 74.668 88.9765 74.8789C88.7695 75.0898 88.5 75.2637 88.1679 75.4004C87.8359 75.5332 87.4375 75.5996 86.9726 75.5996H86.0468V74.9434ZM86.0468 75.834V75.1836H86.9726C87.5156 75.1836 87.9648 75.248 88.3203 75.377C88.6757 75.5059 88.955 75.6777 89.1582 75.8926C89.3652 76.1074 89.5097 76.3438 89.5918 76.6016C89.6777 76.8555 89.7207 77.1094 89.7207 77.3633C89.7207 77.7617 89.6523 78.1152 89.5156 78.4238C89.3828 78.7324 89.1933 78.9941 88.9472 79.209C88.705 79.4238 88.4199 79.5859 88.0918 79.6953C87.7636 79.8047 87.4062 79.8594 87.0195 79.8594C86.6484 79.8594 86.2988 79.8066 85.9707 79.7012C85.6464 79.5957 85.3593 79.4434 85.1093 79.2441C84.8593 79.041 84.664 78.793 84.5234 78.5C84.3828 78.2031 84.3125 77.8652 84.3125 77.4863H85.3964C85.3964 77.7832 85.4609 78.043 85.5898 78.2656C85.7226 78.4883 85.9101 78.6621 86.1523 78.7871C86.3984 78.9082 86.6875 78.9688 87.0195 78.9688C87.3515 78.9688 87.6367 78.9121 87.875 78.7988C88.1171 78.6816 88.3027 78.5059 88.4316 78.2715C88.5644 78.0371 88.6308 77.7422 88.6308 77.3867C88.6308 77.0312 88.5566 76.7402 88.4082 76.5137C88.2597 76.2832 88.0488 76.1133 87.7753 76.0039C87.5058 75.8906 87.1875 75.834 86.8203 75.834H86.0468Z"
        fill={connectorStateColors?.[getConnectorState(leftTurnHazard)]?.connectorIdentifier}/>

      {/* 4. This is the Stop Lamps Connector graphic */}
      <path xmlns="http://www.w3.org/2000/svg"
        d="M58.0002 103.393C63.7632 103.393 68.435 98.7212 68.435 92.9582C68.435 87.1952 63.7632 82.5234 58.0002 82.5234C52.2372 82.5234 47.5654 87.1952 47.5654 92.9582C47.5654 98.7212 52.2372 103.393 58.0002 103.393Z"
        fill={connectorStateColors?.[getConnectorState(stopLamps)]?.connectorPerimeterFill}
        stroke={dlsColor('grey3')}
        strokeWidth="0.5"/>
      <path d="M58.0001 100.549C62.1913 100.549 65.589 97.1513 65.589 92.96C65.589 88.7688 62.1913 85.3711 58.0001 85.3711C53.8088 85.3711 50.4111 88.7688 50.4111 92.96C50.4111 97.1513 53.8088 100.549 58.0001 100.549Z"
        fill={connectorStateColors?.[getConnectorState(stopLamps)]?.connectorInteriorFill}
        stroke={connectorStateColors?.[getConnectorState(stopLamps)]?.connectorInteriorStroke}
        strokeWidth="0.5"/>
      <path xmlns="http://www.w3.org/2000/svg"
        d="M61.0127 94.043V94.9336H54.8486V94.2949L58.6689 88.3828H59.5537L58.6044 90.0938L56.0791 94.043H61.0127ZM59.8232 88.3828V96.9141H58.7392V88.3828H59.8232Z"
        fill={connectorStateColors?.[getConnectorState(stopLamps)]?.connectorIdentifier}/>

      {/* 5. This is the Right Turn & Hazard Connector graphic */}
      <path d="M29.3049 86.0024C35.0679 86.0024 39.7397 81.3306 39.7397 75.5676C39.7397 69.8046 35.0679 65.1328 29.3049 65.1328C23.5419 65.1328 18.8701 69.8046 18.8701 75.5676C18.8701 81.3306 23.5419 86.0024 29.3049 86.0024Z"
        fill={connectorStateColors?.[getConnectorState(rightTurnHazard)]?.connectorPerimeterFill}
        stroke={dlsColor('grey3')}
        strokeWidth="0.5"/>
      <path d="M29.3048 83.1583C33.496 83.1583 36.8937 79.7607 36.8937 75.5694C36.8937 71.3781 33.496 67.9805 29.3048 67.9805C25.1135 67.9805 21.7158 71.3781 21.7158 75.5694C21.7158 79.7607 25.1135 83.1583 29.3048 83.1583Z"
        fill={connectorStateColors?.[getConnectorState(rightTurnHazard)]?.connectorInteriorFill}
        stroke={connectorStateColors?.[getConnectorState(rightTurnHazard)]?.connectorInteriorStroke}
        strokeWidth="0.5"/>
      <path d="M27.5752 75.6816L26.708 75.459L27.1357 71.2109H31.5127V72.2129H28.0556L27.7978 74.5332C27.9541 74.4434 28.1513 74.3594 28.3896 74.2812C28.6318 74.2031 28.9091 74.1641 29.2216 74.1641C29.6162 74.1641 29.9697 74.2324 30.2822 74.3691C30.5947 74.502 30.8603 74.6934 31.0791 74.9434C31.3017 75.1934 31.4716 75.4941 31.5888 75.8457C31.706 76.1973 31.7646 76.5898 31.7646 77.0234C31.7646 77.4336 31.708 77.8105 31.5947 78.1543C31.4853 78.498 31.3193 78.7988 31.0966 79.0566C30.874 79.3105 30.5927 79.5078 30.2529 79.6484C29.9169 79.7891 29.5205 79.8594 29.0634 79.8594C28.7197 79.8594 28.3935 79.8125 28.0849 79.7188C27.7802 79.6211 27.5068 79.4746 27.2646 79.2793C27.0263 79.0801 26.831 78.834 26.6787 78.541C26.5302 78.2441 26.4365 77.8965 26.3974 77.498H27.4287C27.4755 77.8184 27.5693 78.0879 27.7099 78.3066C27.8505 78.5254 28.0341 78.6914 28.2607 78.8047C28.4912 78.9141 28.7587 78.9688 29.0634 78.9688C29.3212 78.9688 29.5498 78.9238 29.749 78.834C29.9482 78.7441 30.1162 78.6152 30.2529 78.4473C30.3896 78.2793 30.4931 78.0762 30.5634 77.8379C30.6377 77.5996 30.6748 77.332 30.6748 77.0352C30.6748 76.7656 30.6377 76.5156 30.5634 76.2852C30.4892 76.0547 30.3779 75.8535 30.2294 75.6816C30.0849 75.5098 29.9072 75.377 29.6962 75.2832C29.4853 75.1855 29.2431 75.1367 28.9697 75.1367C28.6064 75.1367 28.331 75.1855 28.1435 75.2832C27.9599 75.3809 27.7705 75.5137 27.5752 75.6816Z"
        fill={connectorStateColors?.[getConnectorState(rightTurnHazard)]?.connectorIdentifier}/>

      {/* 6. This is the Tail & Running Lights Connector graphic */}
      <path d="M29.3049 54.6977C35.0679 54.6977 39.7397 50.0259 39.7397 44.2629C39.7397 38.4999 35.0679 33.8281 29.3049 33.8281C23.5419 33.8281 18.8701 38.4999 18.8701 44.2629C18.8701 50.0259 23.5419 54.6977 29.3049 54.6977Z"
        fill={connectorStateColors?.[getConnectorState(tailRunningLights)]?.connectorPerimeterFill}
        stroke={dlsColor('grey3')}
        strokeWidth="0.5"/>
      <path d="M29.3048 51.8536C33.496 51.8536 36.8937 48.456 36.8937 44.2647C36.8937 40.0735 33.496 36.6758 29.3048 36.6758C25.1135 36.6758 21.7158 40.0735 21.7158 44.2647C21.7158 48.456 25.1135 51.8536 29.3048 51.8536Z"
        fill={connectorStateColors?.[getConnectorState(tailRunningLights)]?.connectorInteriorFill}
        stroke={connectorStateColors?.[getConnectorState(tailRunningLights)]?.connectorInteriorStroke}
        strokeWidth="0.5"/>
      <path d="M30.3642 39.8945H30.458V40.8145H30.3642C29.79 40.8145 29.3095 40.9082 28.9228 41.0957C28.5361 41.2793 28.2294 41.5273 28.0029 41.8398C27.7763 42.1484 27.6123 42.4961 27.5107 42.8828C27.413 43.2695 27.3642 43.6621 27.3642 44.0605V45.3145C27.3642 45.6934 27.4091 46.0293 27.499 46.3223C27.5888 46.6113 27.7119 46.8555 27.8681 47.0547C28.0244 47.2539 28.2002 47.4043 28.3955 47.5059C28.5947 47.6074 28.8017 47.6582 29.0166 47.6582C29.2666 47.6582 29.4892 47.6113 29.6845 47.5176C29.8798 47.4199 30.0439 47.2852 30.1767 47.1133C30.3134 46.9375 30.4169 46.7305 30.4873 46.4922C30.5576 46.2539 30.5927 45.9922 30.5927 45.707C30.5927 45.4531 30.5615 45.209 30.499 44.9746C30.4365 44.7363 30.3408 44.5254 30.2119 44.3418C30.083 44.1543 29.9209 44.0078 29.7255 43.9023C29.5341 43.793 29.3056 43.7383 29.04 43.7383C28.7392 43.7383 28.458 43.8125 28.1962 43.9609C27.9384 44.1055 27.7255 44.2969 27.5576 44.5352C27.3935 44.7695 27.2998 45.0254 27.2763 45.3027L26.7021 45.2969C26.7568 44.8594 26.8584 44.4863 27.0068 44.1777C27.1591 43.8652 27.3466 43.6113 27.5693 43.416C27.7959 43.2168 28.0478 43.0723 28.3252 42.9824C28.6064 42.8887 28.9033 42.8418 29.2158 42.8418C29.6416 42.8418 30.0087 42.9219 30.3173 43.082C30.6259 43.2422 30.8798 43.457 31.0791 43.7266C31.2783 43.9922 31.4248 44.293 31.5185 44.6289C31.6162 44.9609 31.665 45.3027 31.665 45.6543C31.665 46.0566 31.6084 46.4336 31.4951 46.7852C31.3818 47.1367 31.2119 47.4453 30.9853 47.7109C30.7627 47.9766 30.4873 48.1836 30.1591 48.332C29.831 48.4805 29.4502 48.5547 29.0166 48.5547C28.5556 48.5547 28.1533 48.4609 27.8095 48.2734C27.4658 48.082 27.1806 47.8281 26.9541 47.5117C26.7275 47.1953 26.5576 46.8438 26.4443 46.457C26.331 46.0703 26.2744 45.6777 26.2744 45.2793V44.7695C26.2744 44.168 26.3349 43.5781 26.456 43C26.5771 42.4219 26.7861 41.8984 27.083 41.4297C27.3837 40.9609 27.7998 40.5879 28.331 40.3105C28.8623 40.0332 29.54 39.8945 30.3642 39.8945Z"
        fill={connectorStateColors?.[getConnectorState(tailRunningLights)]?.connectorIdentifier}/>

      {/* 7. This is the Auxiliary Connector graphic */}
      <path d="M58.0002 70.35C63.7632 70.35 68.435 65.6782 68.435 59.9153C68.435 54.1523 63.7632 49.4805 58.0002 49.4805C52.2372 49.4805 47.5654 54.1523 47.5654 59.9153C47.5654 65.6782 52.2372 70.35 58.0002 70.35Z"
        fill={connectorStateColors?.[getConnectorState(auxiliary)]?.connectorPerimeterFill}
        stroke={dlsColor('grey3')}
        strokeWidth="0.5"/>
      <path d="M58.0001 67.506C62.1913 67.506 65.589 64.1083 65.589 59.9171C65.589 55.7258 62.1913 52.3281 58.0001 52.3281C53.8088 52.3281 50.4111 55.7258 50.4111 59.9171C50.4111 64.1083 53.8088 67.506 58.0001 67.506Z"
        fill={connectorStateColors?.[getConnectorState(auxiliary)]?.connectorInteriorFill}
        stroke={connectorStateColors?.[getConnectorState(auxiliary)]?.connectorInteriorStroke}
        strokeWidth="0.5"/>
      <path d="M60.7607 56.3828V56.9922L57.2275 64.9141H56.0849L59.6123 57.2734H54.9951V56.3828H60.7607Z"
        fill={connectorStateColors?.[getConnectorState(auxiliary)]?.connectorIdentifier}/>

      {/* This is the Transparent Circle over the Ground Connector */}
      <path onMouseOver={function() {
        onConnectorHover('ground');
      }}
      onMouseOut={function() {
        onConnectorHover(null);
      }}
      id="path-m3goywq7v8c"
      d="M58.0002,37.3071C63.7632,37.3071,68.435,32.6353,68.435,26.8723C68.435,21.1093,63.7632,16.4375,58.0002,16.4375C52.2372,16.4375,47.5654,21.1093,47.5654,26.8723C47.5654,32.6353,52.2372,37.3071,58.0002,37.3071Z"
      transform="translate(0 0.0018)"
      fill="transparent"
      stroke="none"
      strokeWidth="0.5" />

      {/* This is the Transparent Circle over the Clearance / Side Markers Connector */}
      <path onMouseOver={function() {
        onConnectorHover('clearanceSideMarkers');
      }}
      onMouseOut={function() {
        onConnectorHover(null);
      }}
      id="path-5ipl53xj1e"
      d="M58.0002,37.3071C63.7632,37.3071,68.435,32.6353,68.435,26.8723C68.435,21.1093,63.7632,16.4375,58.0002,16.4375C52.2372,16.4375,47.5654,21.1093,47.5654,26.8723C47.5654,32.6353,52.2372,37.3071,58.0002,37.3071Z"
      transform="translate(28.69515 17.3464)"
      fill="transparent"
      stroke="none"
      strokeWidth="0.5" />

      {/* This is the Transparent Circle over the Left Turn & Hazards Connector */}
      <path onMouseOver={function() {
        onConnectorHover('leftTurnHazard');
      }}
      onMouseOut={function() {
        onConnectorHover(null);
      }}
      id="path-zb5l7tfhq3"
      d="M58.0002,37.3071C63.7632,37.3071,68.435,32.6353,68.435,26.8723C68.435,21.1093,63.7632,16.4375,58.0002,16.4375C52.2372,16.4375,47.5654,21.1093,47.5654,26.8723C47.5654,32.6353,52.2372,37.3071,58.0002,37.3071Z"
      transform="translate(29.0164 48.6971)"
      fill="transparent"
      stroke="none"
      strokeWidth="0.5" />

      {/* This is the Transparent Circle over the Tail & Running Lights Connector */}
      <path onMouseOver={function() {
        onConnectorHover('tailRunningLights');
      }}
      onMouseOut={function() {
        onConnectorHover(null);
      }}
      id="path-v2udnzwwxwt"
      d="M58.0002,37.3071C63.7632,37.3071,68.435,32.6353,68.435,26.8723C68.435,21.1093,63.7632,16.4375,58.0002,16.4375C52.2372,16.4375,47.5654,21.1093,47.5654,26.8723C47.5654,32.6353,52.2372,37.3071,58.0002,37.3071Z"
      transform="translate(-29.0305 17.3523)"
      fill="transparent"
      stroke="none"
      strokeWidth="0.5" />

      {/* This is the Transparent Circle over the Tail & Running Lights Connector */}
      <path onMouseOver={function() {
        onConnectorHover('stopLamps');
      }}
      onMouseOut={function() {
        onConnectorHover(null);
      }}
      id="path-ymhhbis5cx8"
      d="M58.0002,37.3071C63.7632,37.3071,68.435,32.6353,68.435,26.8723C68.435,21.1093,63.7632,16.4375,58.0002,16.4375C52.2372,16.4375,47.5654,21.1093,47.5654,26.8723C47.5654,32.6353,52.2372,37.3071,58.0002,37.3071Z"
      transform="translate(0 66.0859)"
      fill="transparent"
      stroke="none"
      strokeWidth="0.5" />

      {/* This is the Transparent Circle over the Right Turn & Hazards Connector */}
      <path onMouseOver={function() {
        onConnectorHover('rightTurnHazard');
      }}
      onMouseOut={function() {
        onConnectorHover(null);
      }}
      id="path-1dwmr4l3p0y"
      d="M58.0002,37.3071C63.7632,37.3071,68.435,32.6353,68.435,26.8723C68.435,21.1093,63.7632,16.4375,58.0002,16.4375C52.2372,16.4375,47.5654,21.1093,47.5654,26.8723C47.5654,32.6353,52.2372,37.3071,58.0002,37.3071Z"
      transform="translate(-28.69545 48.6971)"
      fill="transparent"
      stroke="none"
      strokeWidth="0.5" />

      {/* This is the Transparent Circle over the Auxiliary Connector */}
      <path onMouseOver={function() {
        onConnectorHover('auxiliary');
      }}
      onMouseOut={function() {
        onConnectorHover(null);
      }}
      id="path-ulq19065vfh"
      d="M58.0002,37.3071C63.7632,37.3071,68.435,32.6353,68.435,26.8723C68.435,21.1093,63.7632,16.4375,58.0002,16.4375C52.2372,16.4375,47.5654,21.1093,47.5654,26.8723C47.5654,32.6353,52.2372,37.3071,58.0002,37.3071Z"
      transform="translate(-0.1223 33.1277)"
      fill="transparent"
      stroke="none"
      strokeWidth="0.5" />
    </svg>
  );
};

export default memo(LightOutDetectionIcon);
